// baseAuthApi — auth + contexto del operador contra lealify-base (re-cableo del manager).
// Reemplaza Firebase Auth + lecturas de users/businesses en Firestore. La sesión va por
// cookie httpOnly de lealify-base; este módulo solo arma los datos para el AuthContext.
import { baseApi } from './baseClient';

const TENANT_SLUG = (import.meta.env.VITE_TENANT_SLUG as string) || undefined;

export interface BasePermissionsResp {
  user?: { id: number; tenant_id: number; role: 'owner' | 'manager' | 'staff'; name: string; email: string };
  platform?: boolean;
  admin?: { id: number; name: string; email: string };   // identidad del super_admin (platform=true)
  role?: string;
  tenant_id?: number;
  modules?: Array<{ key: string; level: string; active: boolean }>;
  /** Sesión "Ver como": quién impersona (owner o plataforma). null/ausente = sesión normal. */
  impersonator?: { id: number; name: string | null; platform?: boolean } | null;
  /** Módulo cross "tours" (coach-marks visto-una-vez): users.tours = { key: fecha }. */
  tours?: Record<string, unknown>;
}
export interface BaseTenantResp {
  businessId: string;        // slug del tenant
  businessName: string;
  logoUrl: string | null;
  colors: { primary: string; secondary: string };
  businessType: string;
  whiteLabel: boolean;
  timeZone?: string;         // zona horaria del negocio (tenants.timezone), p.ej. America/Tijuana
  onboarded?: boolean;       // false → negocio nuevo: dispara el wizard de onboarding
  /** Vertical del negocio: 'lealtad' | 'dental' | 'letsmove' | 'clublectura' | 'pos'.
   *  Define de QUÉ producto le habla la pantalla de suscripción (F1, 13-ago-2026). */
  vertical?: string;
  /** true = su plan venció y quedó en solo lectura (F2): puede consultar, no registrar. */
  planReadOnly?: boolean;
  /** Tope de clientes del plan gratis (21-sep-2026). limit=null → sin tope (PRO/prueba). */
  contactLimit?: ContactLimitInfo;
}
export interface ContactLimitInfo {
  limit: number | null;
  used: number;
  remaining: number | null;
  warn: boolean;
  blocked: boolean;
}
export interface BaseLoyaltySettingsResp {
  settings: {
    loyalty_mode: 'visits' | 'cashback';
    points_per_visit: number;
    cashback_percentage: number;
    time_between_visits_min: number;
    allow_points_accumulation: number;
    birthday_points_mode: 'disabled' | 'manual' | 'automatic';
    request_birthday: number;
    request_email: number;
    request_gender: number;
    referrals_enabled: number;
    points_per_referral: number;
    referral_bonus_new_client: number;
    cashback_special_tag?: string | null;              // cashback especial por etiqueta (17-oct-2026)
    cashback_special_percentage?: number | string | null;
    [k: string]: unknown;
  };
  isPro: boolean;
  social: { socialUrl: string | null; socialType: string };
  legal: { disclaimerEnabled: boolean; disclaimerPrivacyText: string; disclaimerRulesText: string; disclaimerVersion: number };
}
export interface BaseBranch { id: number; name: string; is_active: number; cashback_percentage?: string | number | null; }

export const baseAuthApi = {
  // Login del operador (owner/manager/staff). El tenant sale del host (fachada) en prod;
  // en staging/dev se pasa VITE_TENANT_SLUG. base también resuelve por email único.
  login: (email: string, password: string) =>
    baseApi.post<BasePermissionsResp>('/auth/login', { email, password, tenant_slug: TENANT_SLUG }),
  // Alta self-service de un negocio nuevo: crea tenant + owner + sucursal "Principal" y
  // deja la sesión abierta (auto-login). Manda verificación de correo (no bloqueante).
  signup: (data: { email: string; password: string; name: string; businessName: string; phone?: string; countryCode?: string; timezone?: string | null; gaClientId?: string | null; gaSessionId?: string | null; utm?: Record<string, string> | null; landing?: Record<string, string> | null }) =>
    baseApi.post<BasePermissionsResp & { needsOnboarding?: boolean }>('/auth/signup', data),
  // Login del SUPER_ADMIN de plataforma (Carlos). Crea una sesión de plataforma (sin tenant);
  // luego /me/permissions devuelve platform:true y el AuthContext desbloquea el panel admin.
  platformLogin: (email: string, password: string) =>
    baseApi.post<{ admin: { id: number; name: string; email: string } }>('/auth/platform/login', { email, password }),
  logout: () => baseApi.post<{ ok: boolean }>('/auth/logout'),
  // Marca terminado el wizard de onboarding del negocio (persiste tenants.onboarded=1).
  markOnboarding: () => baseApi.post<{ ok: boolean }>('/me/onboarding-complete', {}),
  // Restaurar sesión al recargar (cookie). 401 = sin sesión.
  permissions: () => baseApi.get<BasePermissionsResp>('/me/permissions'),
  // Sale de "Ver como": mata la sesión impersonada y restaura la de plataforma (lbase_return).
  stopImpersonation: () => baseApi.post<{ ok: true; restored: boolean }>('/auth/stop-impersonation', {}),
  tenant: () => baseApi.get<BaseTenantResp>('/me/tenant'),
  branches: () => baseApi.get<{ branches: BaseBranch[] }>('/me/branches'),
  // Sucursales del negocio para administración (incluye INACTIVAS, a diferencia de /me/branches
  // que filtra is_active=1). Lo usa "Mi Negocio" para poder mostrar/reactivar las desactivadas.
  allBranches: () => baseApi.get<{ branches: BaseBranch[] }>('/biz/branches'),
  loyaltySettings: () => baseApi.get<BaseLoyaltySettingsResp>('/loyalty/settings'),
  // Cuenta de contactos (para "clientes registrados" del panel) — total de la lista.
  contactsCount: () => baseApi.get<{ total: number }>('/contacts?page=1&page_size=1'),
  // Cambio LOGUEADO de contraseña/correo (UserSettingsPage). Reautentica con la actual.
  changePassword: (currentPassword: string, newPassword: string) =>
    baseApi.post<{ ok: boolean }>('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
  changeEmail: (currentPassword: string, newEmail: string) =>
    baseApi.post<{ ok: boolean; email: string }>('/auth/change-email', { current_password: currentPassword, new_email: newEmail }),
  // Recuperación de contraseña (reemplaza el viejo flujo esapi/Firebase). El backend
  // responde 200 siempre (no revela si el correo existe) y manda el correo con enlace a /restablecer.
  forgotPassword: (email: string) =>
    baseApi.post<{ ok: boolean }>('/auth/forgot', { email }),
  // Valida el token del enlace sin consumirlo (la página /restablecer muestra el form). 404 = inválido/vencido.
  peekResetToken: (token: string) =>
    baseApi.get<{ valid: boolean; name?: string; email?: string }>(`/auth/token/reset/${encodeURIComponent(token)}`),
  // Consume el token, fija la nueva contraseña e inicia sesión (cookie). Devuelve user + modules.
  resetPassword: (token: string, password: string) =>
    baseApi.post<BasePermissionsResp>('/auth/reset', { token, password }),
  // ── Activación de operador invitado (kind=invite) — gemelo del reset, pero con el token
  // de invitación. peekInviteToken valida sin consumir; activateAccount fija la contraseña
  // inicial, activa la cuenta e inicia sesión igual que el reset. Destino del enlace /activar?token=.
  peekInviteToken: (token: string) =>
    baseApi.get<{ valid: boolean; name?: string; email?: string }>(`/auth/token/invite/${encodeURIComponent(token)}`),
  activateAccount: (token: string, password: string) =>
    baseApi.post<BasePermissionsResp>('/auth/activar', { token, password }),
  // ── Perfil PROPIO del operador (módulo PERFIL compartido; mismos endpoints que usa la agenda).
  myProfile: () =>
    baseApi.get<{ actor: 'user'; profile: { id: number; name: string; email: string; phone: string | null; avatar_url: string | null } }>('/me/profile'),
  updateMyProfile: (data: { name?: string; phone?: string }) =>
    baseApi.put<{ actor: 'user'; profile: { id: number; name: string; phone: string | null; avatar_url: string | null } }>('/me/profile', data),
  uploadMyAvatar: (imageDataUrl: string) =>
    baseApi.post<{ avatar_url: string }>('/me/avatar', { image: imageDataUrl }),
  // ¿Este número tiene WhatsApp? (badge en vivo de forms; rate-limit 12/min server-side)
  waCheck: (phone: string) =>
    baseApi.post<{ exists: boolean; phone: string }>('/me/wa-check', { phone }),
  myAvatarSrc: (userId: number, ver?: number) => `${baseApi.baseUrl}/avatars/user/${userId}${ver ? `?v=${ver}` : ''}`,
};
